import useSWRV from "swrv";
import { computed } from "vue";
import {
  PasswordChange,
  User,
  UserClient,
  Invest24UserClient,
  UserLabelType,
  Notification,
} from "/@/types";
import { useStore } from "/@/vue/store";
import { axios, fetcher } from "/@/modules/axios";
import { errorHandle } from "/@/modules/error";
import { useRouterUtil } from "./router";
import { useAuthorize } from "./authorize";
import type { MenuItem } from "primevue/menuitem";

//

export function useUser() {
  const store = useStore();

  const { reload, goto, currentRouteName } = useRouterUtil();
  const { logout } = useAuthorize();

  async function handleLogout() {
    await logout();
    reload();
  }

  const user = computed<UserClient | undefined>(() => {
    return store.state.user.user;
  });

  async function setUser(user: UserClient | undefined) {
    await store.dispatch("user/SET_USER", user);
  }

  async function getLoginUser() {
    return await store.dispatch("user/GET_USER");
  }

  function getUser(
    options: { withMemberAnnounces?: boolean; withInvest24?: boolean } = {
      withMemberAnnounces: false,
      withInvest24: false,
    }
  ) {
    const { withMemberAnnounces, withInvest24 } = options;

    let uri = "/api/v1/current_user?";

    if (withMemberAnnounces) {
      uri += "with_member_announces=true&";
    }

    if (withInvest24) {
      uri += "with_invest24=true&";
    }

    return useSWRV<UserClient>(uri, fetcher);
  }

  function getUsers() {
    return useSWRV<UserClient[]>("/api/v1/users", fetcher);
  }

  function getInvest24Users() {
    return useSWRV<Invest24UserClient[]>("/api/v1/invest24_users", fetcher);
  }

  async function changePassword(form: PasswordChange) {
    try {
      await axios.patch("/api/v1/users/change_password", {
        user: form,
      });
      return true;
    } catch (e) {
      errorHandle(e);
      return false;
    }
  }

  async function updateUser(user: User) {
    try {
      await axios.patch("/api/v1/users", {
        user,
      });
      return true;
    } catch (e) {
      errorHandle(e);
      return false;
    }
  }

  async function sendResetPasswordEmail(email: string) {
    try {
      await axios.post("/api/v1/users/send_reset_password_email", {
        user: {
          email,
        },
      });
      return true;
    } catch (e) {
      errorHandle(e);
      return false;
    }
  }

  async function resetPassword(
    form: PasswordChange,
    { token }: { token: string }
  ) {
    try {
      await axios.patch(`/api/v1/users/reset_password?token=${token}`, {
        user: form,
      });
      return true;
    } catch (e) {
      errorHandle(e);
      return false;
    }
  }

  async function unsubscribe() {
    try {
      await axios.delete("/api/v1/users/unsubscribe");
      return true;
    } catch (e) {
      errorHandle(e);
      return false;
    }
  }

  //

  function hasLabel(user: UserClient, labelType: UserLabelType): boolean {
    return user.userLabels.map((ul) => ul.labelType).includes(labelType);
  }

  const isSystemAdmin = computed(() => {
    if (!user.value) return false;

    return hasLabel(user.value, "system_admin");
  });

  const isAllManager = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value) return true;

    return hasLabel(user.value, "all_manager");
  });

  const isAllPoster = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return hasLabel(user.value, "all_poster");
  });

  const isAllLManager = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return hasLabel(user.value, "all_l_manager");
  });

  const isAdmin = computed(() => {
    if (isSystemAdmin.value) return true;

    return user.value?.admin;
  });

  const isOwner = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value) return true;

    return hasLabel(user.value, "owner");
  });

  const isOffice = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return hasLabel(user.value, "office");
  });

  const isUserManager = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return hasLabel(user.value, "u_manager");
  });

  const isVManager = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return hasLabel(user.value, "v_manager");
  });

  const isAManager = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return hasLabel(user.value, "a_manager");
  });

  const isBManager = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return hasLabel(user.value, "b_manager");
  });

  const isCManager = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return hasLabel(user.value, "c_manager");
  });

  const isABCManager = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return isAManager.value || isBManager.value || isCManager.value;
  });

  const isAPoster = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return hasLabel(user.value, "a_poster");
  });

  const isBPoster = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return hasLabel(user.value, "b_poster");
  });

  const isCPoster = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return hasLabel(user.value, "c_poster");
  });

  const isABCPoster = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return isAPoster.value || isBPoster.value || isCPoster.value;
  });

  const isATManager = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return hasLabel(user.value, "a_t_manager");
  });

  const isBTManager = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return hasLabel(user.value, "b_t_manager");
  });

  const isCTManager = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return hasLabel(user.value, "c_t_manager");
  });

  const isABCTManager = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;
    return isATManager.value || isBTManager.value || isCTManager.value;
  });

  const isIManager = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value || isAManager.value)
      return true;

    return hasLabel(user.value, "i_manager");
  });

  const isDManager = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return hasLabel(user.value, "d_manager");
  });

  const isOrganizationManager = computed(() => {
    return isCManager.value;
  });

  const isBcpManager = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return hasLabel(user.value, "bcp_manager");
  });

  const isRuleManager = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return hasLabel(user.value, "rule_manager");
  });

  const canEditNews = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return isAllPoster.value || isABCManager.value || isABCPoster.value;
  });

  const isSomeManager = computed(() => {
    if (!user.value) return false;
    if (isSystemAdmin.value || isAllManager.value) return true;

    return (
      isOffice.value ||
      isAllManager.value ||
      isAllPoster.value ||
      isUserManager.value ||
      isAllLManager.value ||
      isOrganizationManager.value ||
      isABCManager.value ||
      isABCTManager.value ||
      isABCPoster.value ||
      isVManager.value ||
      isBcpManager.value ||
      isRuleManager.value ||
      isDManager.value ||
      isIManager.value
    );
  });

  // notification

  const notifications = computed(() => {
    if (!user.value) {
      return [];
    }

    return user.value.notifications;
  });

  const unreadNotifications = computed(() => {
    return (
      notifications.value?.filter((notification) => !notification.read) || []
    );
  });

  async function updateNotification(id: number, prms: Partial<Notification>) {
    try {
      await axios.patch(`/api/v1/notifications/${id}`, {
        notification: prms,
      });
      return true;
    } catch (error) {
      errorHandle(error);
      return false;
    }
  }

  function gotoNotifiable(
    notification: Notification,
    options: { name: string; params?: Record<string, any> } = {
      name: "Index",
    }
  ) {
    const { name, params } = options;

    if (notification.actionType === "new_registration") {
      goto({
        name: "UsersSystemAdminUsers",
      });
    } else if (notification.actionType === "new_inquiry") {
      goto({
        name: "UsersInquiriesShow",
        params: {
          id: notification.notifiableId,
        },
      });
    } else if (notification.actionType === "new_inquiry_reply") {
      goto({
        name: "UsersInquiriesShow",
        params: {
          id: notification.notifiableId,
        },
      });
    } else if (notification.actionType === "update_user_info") {
      goto({
        name: "UsersSystemAdminUsers",
      });
    } else if (notification.actionType === "unsubscribe_user") {
      goto({
        name: "UsersSystemAdminUsers",
      });
    } else if (notification.notifiableType === "News") {
      goto({
        name: "UsersNewsShow",
        params: {
          id: notification.notifiableId,
        },
      });
    } else if (notification.notifiableType === "Training") {
      goto({
        name: "UsersTrainingsShow",
        params: {
          id: notification.notifiableId,
        },
      });
    } else if (notification.notifiableType === "Inquiry") {
      goto({
        name: "UsersInquiriesShow",
        params: {
          id: notification.notifiableId,
        },
      });
    } else if (notification.notifiableType === "Bcp") {
      goto({
        name: "UsersBcpShow",
        params: {
          id: notification.notifiableId,
        },
      });
    } else if (notification.notifiableType === "Rule") {
      goto({
        name: "UsersRulesShow",
        params: {
          id: notification.notifiableId,
        },
      });
    } else if (notification.notifiableType === "Sponsor") {
      goto({
        name: "UsersSponsorsShow",
        params: {
          id: notification.notifiableId,
        },
      });
    } else if (notification.notifiableType === "Magazine") {
      goto({
        name: "UsersMagazinesShow",
        params: {
          id: notification.notifiableId,
        },
      });
    } else if (notification.notifiableType === "Organization") {
      goto({
        name: "UsersOrganizationsShow",
        params: {
          id: notification.notifiableId,
        },
      });
    }
  }

  function notifiableIcon(
    notification: Notification,
    options: { name: string; params?: Record<string, any> } = {
      name: "Index",
    }
  ) {
    const { name, params } = options;

    if (notification.notifiableType === "News") {
      return "pi pi-bell";
    } else if (notification.notifiableType === "Training") {
      return "pi pi-video";
    } else if (notification.notifiableType === "Inquiry") {
      return "pi pi-exclamation-circle";
    } else if (notification.notifiableType === "Bcp") {
      return "pi pi-file-check";
    } else if (notification.notifiableType === "Rule") {
      return "pi pi-inbox";
    } else if (notification.notifiableType === "Sponsor") {
      return "pi pi-users";
    } else if (notification.notifiableType === "Magazine") {
      return "pi pi-book";
    } else if (notification.notifiableType === "Organization") {
      return "pi pi-sitemap";
    } else if (notification.notifiableType === "User") {
      return "pi pi-user";
    }
  }

  // header
  const headerItems = computed<MenuItem[]>(() => {
    return [
      {
        //icon: "pi pi-caret-down",
        key: "Index",
        label: "一般メニュー",
        activeName: ["Index"],
        visible: !!user.value,
        items: [
          {
            label: "お知らせ",
            icon: "pi pi-bell",
            visible: !user.value,
            command: () =>
              goto({
                name: "NewsIndex",
              }),
            /*
      items: props.organizations.map((org) => ({
        to: {
          name: "NewsIndex",
          query: { organizationId: org.id },
        },
        label: org.name,
        icon: "pi pi-minus",
      })),
      */
            activeName: ["NewsIndex"],
          },
          {
            icon: "pi pi-sitemap",
            label: "協会概要",
            command: () =>
              goto({
                name: "About",
              }),
            /*
      items: [
        {
          href: props.latestMagazine?.attachedPdfs[0].url,
          visible: !!props.latestMagazine,
          icon: "pi pi-minus",
          label: "最新号",
        },
        {
          to: { name: "MagazinesIndex" },
          label: "バックナンバー",
          icon: "pi pi-minus",
        },
      ],
      */
            activeName: ["Abount"],
          },
          {
            icon: "pi pi-book",
            label: "広報誌『わ』",
            activeName: ["MagazinesIndex"],
            command: () =>
              goto({
                name: "MagazinesIndex",
              }),
          },
          {
            icon: "pi pi-users",
            label: "賛助会員",
            activeName: ["Sponsor"],
            command: () =>
              goto({
                name: "Sponsor",
              }),
          },
          {
            icon: "pi pi-info-circle",
            label: "お問い合わせ",
            activeName: ["Inquiry"],
            command: () =>
              goto({
                name: "Inquiry",
              }),
          },
        ],
      },
      {
        // icon: "pi pi-caret-down",
        label: "会員メニュー",
        visible: !!user.value,
        key: "UsersMenu",
        badge: !!user.value?.haveUnread ? "!" : undefined,
        items: [
          {
            icon: "pi pi-bell",
            label: "お知らせ",
            badge: !!user.value?.haveUnread ? "!" : undefined,
            command: () => {
              goto({
                name: "UsersNewsUserNewsIndex",
              });
            },
          },
          {
            label: "研修一覧",
            icon: "pi pi-video",
            visible: true,
            command: () => {
              goto({
                name: "UsersTrainingsIndex",
              });
            },
          },
          {
            label: "ＢＣＰ",
            icon: "pi pi-file-check",
            command: () => {
              goto({
                name: "UsersBcpIndex",
              });
            },
          },
          {
            label: "協会書類等",
            icon: "pi pi-inbox",
            command: () => {
              goto({
                name: "UsersRulesIndex",
              });
            },
          },
        ],
        activeName: [
          "UsersNewsUserNewsIndex",
          "UsersBcpIndex",
          "UsersRulesIndex",
          "UsersShow",
        ],
      },
      {
        key: "UsersSystemAdminMenu",
        label: "管理者メニュー",
        // icon: "pi pi-bars",
        visible: !!isSomeManager.value,
        expand: true,
        items: [
          {
            key: "UsersManagementIndex",
            label: "管理者メニュー",
            icon: "pi pi-address-book",
            visible: isSomeManager.value,
            command: () => goto({ name: "UsersManagementIndex" }),
          },
          {
            key: "UsersSystemAdminShow",
            label: "管理者情報",
            icon: "pi pi-sparkles",
            visible: isSomeManager.value,
            command: () => goto({ name: "UsersSystemAdminShow" }),
          },
          {
            key: "UsersNews",
            label: "お知らせ管理",
            icon: "pi pi-bell",
            visible: isAllPoster.value || isABCPoster.value,
            command: () => goto({ name: "UsersNewsIndex" }),
          },
          {
            key: "UsersOrganizationsIndex",
            label: "組織管理",
            icon: "pi pi-sitemap",
            visible: !!isABCManager.value || !!isOrganizationManager.value,
            command: () => goto({ name: "UsersOrganizationsIndex" }),
            class: {
              "p-menuitem-active":
                currentRouteName.value === "UsersOrganizationsIndex",
            },
          },
          {
            key: "UsersSystemAdminUsers",
            label: "会員管理",
            icon: "pi pi-users",
            visible:
              !!isOffice.value ||
              !!isABCManager.value ||
              !!isUserManager.value ||
              !!isAllLManager.value,
            command: () => goto({ name: "UsersSystemAdminUsers" }),
            class: {
              "p-menuitem-active":
                currentRouteName.value === "UsersSystemAdminUsers",
            },
          },
          {
            key: "UsersAdminContents",
            label: "各種管理",
            icon: "pi pi-cog",
            visible:
              isIManager.value ||
              isBcpManager.value ||
              isRuleManager.value ||
              isABCTManager.value,
            items: [
              {
                key: "UsersMagazinesIndex",
                label: "広報誌『わ』管理",
                // icon: "pi pi-book",
                icon: "pi pi-minus",
                visible: !!isRuleManager.value,
                command: () => goto({ name: "UsersMagazinesIndex" }),
                class: {
                  "p-menuitem-active":
                    currentRouteName.value === "UsersMagazinesIndex",
                },
              },
              {
                key: "UsersSystemAdminTrainingsIndex",
                label: "研修管理",
                // icon: "pi pi-video",
                icon: "pi pi-minus",
                visible: !!isABCTManager.value,
                command: () => goto({ name: "UsersSystemAdminTrainingsIndex" }),
                class: {
                  "p-menuitem-active":
                    currentRouteName.value === "UsersSystemAdminTrainingsIndex",
                },
              },
              {
                key: "UsersSponsorsIndex",
                label: "賛助会員管理",
                // icon: "pi pi-users",
                icon: "pi pi-minus",
                visible: !!isRuleManager.value,
                command: () => goto({ name: "UsersSponsorsIndex" }),
                class: {
                  "p-menuitem-active":
                    currentRouteName.value === "UsersSponsorsIndex",
                },
              },
              {
                key: "UsersSystemAdminBcpIndex",
                label: "ＢＣＰ管理",
                // icon: "pi pi-file-check",
                icon: "pi pi-minus",
                visible: !!isBcpManager.value,
                command: () => goto({ name: "UsersSystemAdminBcpIndex" }),
                class: {
                  "p-menuitem-active":
                    currentRouteName.value === "UsersSystemAdminBcpIndex",
                },
              },
              {
                key: "UsersSystemAdminRulesIndex",
                label: "協会書類等管理",
                // icon: "pi pi-inbox",
                icon: "pi pi-minus",
                visible: !!isRuleManager.value,
                command: () => goto({ name: "UsersSystemAdminRulesIndex" }),
                class: {
                  "p-menuitem-active":
                    currentRouteName.value === "UsersSystemAdminRulesIndex",
                },
              },
              {
                key: "UsersInquiriesIndex",
                label: "お問い合わせ管理",
                icon: "pi pi-minus",
                // icon: "pi pi-info-circle",
                visible: !!isIManager.value,
                command: () => goto({ name: "UsersInquiriesIndex" }),
                class: {
                  "p-menuitem-active":
                    currentRouteName.value === "UsersInquiriesIndex",
                },
              },
            ],
          },
          {
            label: "サイトトップ",
            icon: "pi pi-home",
            visible: !!user.value,
            command: () => goto({ name: "Index" }),
          },
          {
            label: "ログアウト",
            icon: "pi pi-sign-out",
            visible: !!user.value,
            command: () => handleLogout(),
          },
        ],
      },
    ];
  });

  return {
    user,
    setUser,
    getLoginUser,
    getUser,
    getUsers,
    getInvest24Users,
    updateUser,
    changePassword,
    resetPassword,
    unsubscribe,
    sendResetPasswordEmail,

    isAllManager,
    isAllPoster,
    isAllLManager,
    isOffice,
    isUserManager,
    isAdmin,
    isVManager,
    isOwner,
    isSystemAdmin,
    isIManager,
    isAManager,
    isBManager,
    isCManager,
    isDManager,
    isABCManager,
    isOrganizationManager,
    isAPoster,
    isBPoster,
    isCPoster,
    isABCPoster,
    isATManager,
    isBTManager,
    isCTManager,
    isABCTManager,
    isBcpManager,
    isRuleManager,
    isSomeManager,

    canEditNews,

    // header
    headerItems,

    // notifications
    notifications,
    unreadNotifications,
    updateNotification,
    gotoNotifiable,
    notifiableIcon,
  };
}
